import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import AdminLayout from "../components/AdminLayout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { BRAZILIAN_STATES, buildCityLabel, fetchCitiesByUf, parseCityAndUf } from "../lib/brLocations";
import { toast } from "sonner";
import { 
  Users, 
  Mail,
  MapPin,
  ToggleLeft,
  ToggleRight,
  UserPlus,
  Briefcase,
  Pencil
} from "lucide-react";

const AREAS = [
  "Financeiro",
  "Planejamento Estratégico",
  "Gestão Comercial",
  "Recursos Humanos",
  "Administração",
  "Produção",
  "Logística",
  "Tecnologia",
  "Jurídico",
  "Marketing",
  "Compras",
  "NR-1 Gestão de Riscos Ocupacionais",
  "Visão de Futuro"
];

const getAreaLabel = (area) => {
  if (area === "Tecnologia") return "Tecnologia/IA";
  return area;
};

const AdminConsultants = () => {
  const [consultants, setConsultants] = useState([]);
  const [referralsByConsultant, setReferralsByConsultant] = useState({});
  const [loading, setLoading] = useState(true);
  const [showActive, setShowActive] = useState(true);
  const [showNewConsultant, setShowNewConsultant] = useState(false);
  const [newConsultant, setNewConsultant] = useState({
    name: "",
    email: "",
    password: "",
    areas_supported: [],
    state_uf: "",
    city: "",
    online_only: false,
    bio: "",
    consultant_split_percent: 70,
    company_split_percent: 30
  });
  const [creating, setCreating] = useState(false);
  const [showEditConsultant, setShowEditConsultant] = useState(false);
  const [editingConsultant, setEditingConsultant] = useState(null);
  const [editConsultant, setEditConsultant] = useState({
    areas_supported: [],
    state_uf: "",
    city: "",
    online_only: false,
    bio: "",
    consultant_split_percent: 70,
    company_split_percent: 30
  });
  const [savingEdit, setSavingEdit] = useState(false);
  const [newConsultantCities, setNewConsultantCities] = useState([]);
  const [editConsultantCities, setEditConsultantCities] = useState([]);
  const [loadingNewCities, setLoadingNewCities] = useState(false);
  const [loadingEditCities, setLoadingEditCities] = useState(false);

  const fetchConsultants = useCallback(async () => {
    try {
      const [consultantsResponse, referralsResponse] = await Promise.all([
        axios.get(`${API}/consultants?active_only=${showActive}`, { withCredentials: true }),
        axios.get(`${API}/referrals`, { withCredentials: true })
      ]);

      const consultantsList = consultantsResponse.data.consultants || [];
      const referrals = referralsResponse.data.referrals || [];
      const groupedReferrals = referrals.reduce((acc, referral) => {
        if (!referral.consultant_id) return acc;
        if (!acc[referral.consultant_id]) acc[referral.consultant_id] = [];
        acc[referral.consultant_id].push(referral);
        return acc;
      }, {});

      setConsultants(consultantsList);
      setReferralsByConsultant(groupedReferrals);
    } catch (error) {
      console.error("Error fetching consultants:", error);
      toast.error("Erro ao carregar consultores");
    } finally {
      setLoading(false);
    }
  }, [showActive]);

  useEffect(() => {
    fetchConsultants();
  }, [fetchConsultants]);

  useEffect(() => {
    if (!newConsultant.state_uf) {
      setNewConsultantCities([]);
      return;
    }

    let isActive = true;
    setLoadingNewCities(true);
    fetchCitiesByUf(newConsultant.state_uf)
      .then((cities) => {
        if (isActive) setNewConsultantCities(cities);
      })
      .catch((error) => {
        console.error("Error fetching new consultant cities:", error);
        if (isActive) {
          setNewConsultantCities([]);
          toast.error("Não foi possível carregar as cidades da UF selecionada");
        }
      })
      .finally(() => {
        if (isActive) setLoadingNewCities(false);
      });

    return () => {
      isActive = false;
    };
  }, [newConsultant.state_uf]);

  useEffect(() => {
    if (!editConsultant.state_uf) {
      setEditConsultantCities([]);
      return;
    }

    let isActive = true;
    setLoadingEditCities(true);
    fetchCitiesByUf(editConsultant.state_uf)
      .then((cities) => {
        if (isActive) setEditConsultantCities(cities);
      })
      .catch((error) => {
        console.error("Error fetching edit consultant cities:", error);
        if (isActive) {
          setEditConsultantCities([]);
          toast.error("Não foi possível carregar as cidades da UF selecionada");
        }
      })
      .finally(() => {
        if (isActive) setLoadingEditCities(false);
      });

    return () => {
      isActive = false;
    };
  }, [editConsultant.state_uf]);

  const handleCreateConsultant = async (e) => {
    e.preventDefault();
    
    if (newConsultant.areas_supported.length === 0) {
      toast.error("Selecione pelo menos uma área de atuação");
      return;
    }
    
    setCreating(true);
    
    try {
      // 1. Create user with consultant role
      const userResponse = await axios.post(`${API}/auth/register`, {
        name: newConsultant.name,
        email: newConsultant.email,
        password: newConsultant.password,
        role: "consultant"
      }, { withCredentials: true });
      
      // 2. Create consultant profile using the new user's token
      await axios.post(`${API}/consultants`, {
        areas_supported: newConsultant.areas_supported,
        city: newConsultant.city ? buildCityLabel(newConsultant.city, newConsultant.state_uf) : null,
        online_only: newConsultant.online_only,
        bio: newConsultant.bio || null,
        consultant_split_percent: Number(newConsultant.consultant_split_percent || 70),
        company_split_percent: Number(newConsultant.company_split_percent || 30)
      }, { 
        withCredentials: true,
        headers: { Authorization: `Bearer ${userResponse.data.token}` }
      });
      
      toast.success("Consultor cadastrado com sucesso!");
      setShowNewConsultant(false);
      setNewConsultant({
        name: "",
        email: "",
        password: "",
        areas_supported: [],
        state_uf: "",
        city: "",
        online_only: false,
        bio: "",
        consultant_split_percent: 70,
        company_split_percent: 30
      });
      fetchConsultants();
    } catch (error) {
      console.error("Error creating consultant:", error);
      toast.error(error.response?.data?.detail || "Erro ao cadastrar consultor");
    } finally {
      setCreating(false);
    }
  };

  const toggleConsultantStatus = async (consultantId) => {
    try {
      const response = await axios.put(`${API}/consultants/${consultantId}/toggle-active`, {}, { withCredentials: true });
      toast.success(response.data.active ? "Consultor ativado" : "Consultor desativado");
      fetchConsultants();
    } catch (error) {
      console.error("Error toggling status:", error);
      toast.error("Erro ao alterar status");
    }
  };

    const toggleArea = (area) => {
    setNewConsultant(prev => ({
      ...prev,
      areas_supported: prev.areas_supported.includes(area)
        ? prev.areas_supported.filter(a => a !== area)
        : [...prev.areas_supported, area]
    }));
  };

  const toggleEditArea = (area) => {
    setEditConsultant((prev) => ({
      ...prev,
      areas_supported: prev.areas_supported.includes(area)
        ? prev.areas_supported.filter((a) => a !== area)
        : [...prev.areas_supported, area]
    }));
  };

  const openEditConsultantDialog = (consultant) => {
    const parsedLocation = parseCityAndUf(consultant.city);
    setEditingConsultant(consultant);
    setEditConsultant({
      areas_supported: consultant.areas_supported || [],
      state_uf: parsedLocation.uf || "",
      city: parsedLocation.city || "",
      online_only: !!consultant.online_only,
      bio: consultant.bio || "",
      consultant_split_percent: consultant.consultant_split_percent ?? 70,
      company_split_percent: consultant.company_split_percent ?? 30
    });
    setShowEditConsultant(true);
  };

  const handleUpdateConsultant = async (e) => {
    e.preventDefault();
    if (!editingConsultant) return;

    if (editConsultant.areas_supported.length === 0) {
      toast.error("Selecione pelo menos uma área de atuação");
      return;
    }

    setSavingEdit(true);
    try {
      await axios.put(`${API}/consultants/${editingConsultant.consultant_id}`, {
        areas_supported: editConsultant.areas_supported,
        city: editConsultant.city ? buildCityLabel(editConsultant.city.trim(), editConsultant.state_uf) : null,
        online_only: editConsultant.online_only,
        bio: editConsultant.bio ? editConsultant.bio.trim() : null,
        consultant_split_percent: Number(editConsultant.consultant_split_percent || 70),
        company_split_percent: Number(editConsultant.company_split_percent || 30)
      }, { withCredentials: true });

      toast.success("Cadastro do consultor atualizado");
      setShowEditConsultant(false);
      setEditingConsultant(null);
      fetchConsultants();
    } catch (error) {
      console.error("Error updating consultant:", error);
      toast.error(error.response?.data?.detail || "Erro ao atualizar consultor");
    } finally {
      setSavingEdit(false);
    }
  };

  const getConsultantAssignments = (consultantId) => {
    return referralsByConsultant[consultantId] || [];
  };

  const getStatusBadgeClass = (status) => {
    if (status === "completed") return "bg-emerald-100 text-emerald-800";
    if (status === "in_progress") return "bg-blue-100 text-blue-800";
    return "bg-amber-100 text-amber-800";
  };

  const getStatusLabel = (status) => {
    if (status === "completed") return "Concluído";
    if (status === "in_progress") return "Em andamento";
    return "Novo";
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-4 sm:p-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-serif font-bold text-navy">Gestão de Consultores</h1>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Switch
                checked={!showActive}
                onCheckedChange={(checked) => setShowActive(!checked)}
                data-testid="show-inactive-switch"
              />
              <Label className="text-sm text-slate-600">Mostrar inativos</Label>
            </div>
            <Dialog open={showNewConsultant} onOpenChange={setShowNewConsultant}>
              <DialogTrigger asChild>
                <Button className="bg-navy hover:bg-navy/90" data-testid="new-consultant-btn">
                  <UserPlus className="h-4 w-4 mr-2" />
                  Novo Consultor
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-serif">Cadastrar Novo Consultor</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateConsultant} className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Nome Completo *</Label>
                      <Input
                        id="name"
                        value={newConsultant.name}
                        onChange={(e) => setNewConsultant({...newConsultant, name: e.target.value})}
                        placeholder="Nome do consultor"
                        required
                        data-testid="consultant-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">E-mail *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={newConsultant.email}
                        onChange={(e) => setNewConsultant({...newConsultant, email: e.target.value})}
                        placeholder="email@exemplo.com"
                        required
                        data-testid="consultant-email-input"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="password">Senha *</Label>
                      <Input
                        id="password"
                        type="password"
                        value={newConsultant.password}
                        onChange={(e) => setNewConsultant({...newConsultant, password: e.target.value})}
                        placeholder="Mínimo 6 caracteres"
                        minLength={6}
                        required
                        data-testid="consultant-password-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="city">Cidade</Label>
                      <div className="grid grid-cols-2 gap-3">
                        <Select
                          value={newConsultant.state_uf}
                          onValueChange={(value) => setNewConsultant({ ...newConsultant, state_uf: value, city: "" })}
                        >
                          <SelectTrigger data-testid="consultant-state-input">
                            <SelectValue placeholder="UF" />
                          </SelectTrigger>
                          <SelectContent>
                            {BRAZILIAN_STATES.map((state) => (
                              <SelectItem key={state.uf} value={state.uf}>
                                {state.uf}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Select
                          value={newConsultant.city}
                          onValueChange={(value) => setNewConsultant({ ...newConsultant, city: value })}
                          disabled={!newConsultant.state_uf || loadingNewCities}
                        >
                          <SelectTrigger data-testid="consultant-city-input">
                            <SelectValue placeholder={loadingNewCities ? "Carregando..." : "Cidade"} />
                          </SelectTrigger>
                          <SelectContent>
                            {newConsultantCities.map((city) => (
                              <SelectItem key={city} value={city}>
                                {city}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="consultant-split">Repasse do consultor (%)</Label>
                      <Input
                        id="consultant-split"
                        type="number"
                        min={0}
                        max={100}
                        value={newConsultant.consultant_split_percent}
                        onChange={(e) => setNewConsultant({...newConsultant, consultant_split_percent: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="company-split">Repasse da empresa (%)</Label>
                      <Input
                        id="company-split"
                        type="number"
                        min={0}
                        max={100}
                        value={newConsultant.company_split_percent}
                        onChange={(e) => setNewConsultant({...newConsultant, company_split_percent: e.target.value})}
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Áreas de Atuação *</Label>
                    <div className="grid grid-cols-2 gap-2 p-3 border rounded-sm max-h-48 overflow-y-auto">
                      {AREAS.map((area) => (
                        <div key={area} className="flex items-center space-x-2">
                          <Checkbox
                            id={`area-${area}`}
                            checked={newConsultant.areas_supported.includes(area)}
                            onCheckedChange={() => toggleArea(area)}
                          />
                          <Label htmlFor={`area-${area}`} className="text-sm cursor-pointer">
                            {getAreaLabel(area)}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="online_only"
                      checked={newConsultant.online_only}
                      onCheckedChange={(checked) => setNewConsultant({...newConsultant, online_only: checked})}
                    />
                    <Label htmlFor="online_only">Atende apenas online</Label>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="bio">Biografia</Label>
                    <Textarea
                      id="bio"
                      value={newConsultant.bio}
                      onChange={(e) => setNewConsultant({...newConsultant, bio: e.target.value})}
                      placeholder="Breve descrição do consultor..."
                      className="h-20"
                      data-testid="consultant-bio-input"
                    />
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full bg-navy hover:bg-navy/90"
                    disabled={creating}
                    data-testid="consultant-submit-btn"
                  >
                    {creating ? "Cadastrando..." : "Cadastrar Consultor"}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {consultants.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-12 text-center">
              <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600 mb-4">
                {showActive ? "Nenhum consultor ativo." : "Nenhum consultor encontrado."}
              </p>
              <Button 
                onClick={() => setShowNewConsultant(true)}
                className="bg-navy hover:bg-navy/90"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Cadastrar Primeiro Consultor
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {consultants.map((consultant) => (
              <Card key={consultant.consultant_id} className="card-hover">
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg font-serif">{consultant.user_name}</CardTitle>
                      <p className="text-sm text-slate-600 flex items-center gap-1 mt-1">
                        <Mail className="h-3 w-3" />
                        {consultant.user_email}
                      </p>
                    </div>
                    <Badge className={consultant.active ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}>
                      {consultant.active ? "Ativo" : "Inativo"}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  {(() => {
                    const assignments = getConsultantAssignments(consultant.consultant_id);
                    return (
                      <div className="mb-4 p-3 bg-slate-50 rounded border">
                        <p className="text-xs text-slate-500 mb-2 flex items-center gap-1">
                          <Briefcase className="h-3 w-3" />
                          Clientes atribuídos
                        </p>
                        <p className="text-sm font-medium text-navy mb-2">
                          {assignments.length} encaminhamento(s)
                        </p>
                        {assignments.length > 0 ? (
                          <div className="space-y-2 max-h-32 overflow-y-auto pr-1">
                            {assignments.map((assignment) => (
                              <Link
                                key={assignment.referral_id}
                                to={`/admin/results/${assignment.diagnostic_id}`}
                                className="block text-xs border rounded p-2 bg-white hover:bg-slate-100 transition-colors"
                                data-testid={`assignment-${assignment.referral_id}`}
                              >
                                <p className="font-medium text-slate-800">
                                  {assignment.company_name || "Cliente sem nome"}
                                </p>
                                <p className="text-slate-600">{getAreaLabel(assignment.area)}</p>
                                <Badge className={`mt-1 text-[10px] ${getStatusBadgeClass(assignment.status)}`}>
                                  {getStatusLabel(assignment.status)}
                                </Badge>
                              </Link>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-slate-500">Nenhum cliente atribuído.</p>
                        )}
                      </div>
                    );
                  })()}

                  <p className="text-sm text-slate-600 flex items-center gap-1 mb-3">
                    <MapPin className="h-3 w-3" />
                    {consultant.city ? consultant.city : "Cidade não cadastrada"}
                    {consultant.online_only && " (Online)"}
                  </p>
                  <p className="text-xs text-slate-500 mb-3">
                    Repasse padrão: {Number(consultant.consultant_split_percent ?? 70).toFixed(0)}% consultor / {Number(consultant.company_split_percent ?? 30).toFixed(0)}% empresa
                  </p>
                  
                  <div className="mb-4">
                    <p className="text-xs text-slate-500 mb-2">Áreas de atuação:</p>
                    <div className="flex flex-wrap gap-1">
                      {consultant.areas_supported?.slice(0, 4).map((area, i) => (
                        <Badge key={i} variant="secondary" className="text-xs">
                          {getAreaLabel(area).length > 15 ? getAreaLabel(area).substring(0, 15) + "..." : getAreaLabel(area)}
                        </Badge>
                      ))}
                      {consultant.areas_supported?.length > 4 && (
                        <Badge variant="secondary" className="text-xs">
                          +{consultant.areas_supported.length - 4}
                        </Badge>
                      )}
                    </div>
                  </div>
                  
                  {consultant.bio && (
                    <p className="text-sm text-slate-600 mb-4 line-clamp-2">{consultant.bio}</p>
                  )}
                  
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEditConsultantDialog(consultant)}
                      data-testid={`edit-consultant-${consultant.consultant_id}`}
                    >
                      <Pencil className="h-4 w-4 mr-2" />
                      Editar
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => toggleConsultantStatus(consultant.consultant_id)}
                      data-testid={`toggle-consultant-${consultant.consultant_id}`}
                    >
                      {consultant.active ? (
                        <>
                          <ToggleLeft className="h-4 w-4 mr-2" />
                          Desativar
                        </>
                      ) : (
                        <>
                          <ToggleRight className="h-4 w-4 mr-2" />
                          Ativar
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
        <Dialog open={showEditConsultant} onOpenChange={setShowEditConsultant}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-serif">Editar Consultor</DialogTitle>
            </DialogHeader>
            {editingConsultant && (
              <form onSubmit={handleUpdateConsultant} className="space-y-4 mt-4">
                <div>
                  <p className="text-sm text-slate-600">Consultor</p>
                  <p className="font-medium text-navy">{editingConsultant.user_name}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-city">Cidade</Label>
                    <div className="grid grid-cols-2 gap-3">
                      <Select
                        value={editConsultant.state_uf}
                        onValueChange={(value) => setEditConsultant({ ...editConsultant, state_uf: value, city: "" })}
                      >
                        <SelectTrigger data-testid="edit-consultant-state-input">
                          <SelectValue placeholder="UF" />
                        </SelectTrigger>
                        <SelectContent>
                          {BRAZILIAN_STATES.map((state) => (
                            <SelectItem key={state.uf} value={state.uf}>
                              {state.uf}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select
                        value={editConsultant.city}
                        onValueChange={(value) => setEditConsultant({ ...editConsultant, city: value })}
                        disabled={!editConsultant.state_uf || loadingEditCities}
                      >
                        <SelectTrigger data-testid="edit-consultant-city-input">
                          <SelectValue placeholder={loadingEditCities ? "Carregando..." : "Cidade"} />
                        </SelectTrigger>
                        <SelectContent>
                          {editConsultantCities.map((city) => (
                            <SelectItem key={city} value={city}>
                              {city}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex items-end">
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="edit-online-only"
                        checked={editConsultant.online_only}
                        onCheckedChange={(checked) => setEditConsultant({ ...editConsultant, online_only: checked })}
                      />
                      <Label htmlFor="edit-online-only">Atende apenas online</Label>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-consultant-split">Repasse do consultor (%)</Label>
                    <Input
                      id="edit-consultant-split"
                      type="number"
                      min={0}
                      max={100}
                      value={editConsultant.consultant_split_percent}
                      onChange={(e) => setEditConsultant({ ...editConsultant, consultant_split_percent: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-company-split">Repasse da empresa (%)</Label>
                    <Input
                      id="edit-company-split"
                      type="number"
                      min={0}
                      max={100}
                      value={editConsultant.company_split_percent}
                      onChange={(e) => setEditConsultant({ ...editConsultant, company_split_percent: e.target.value })}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Áreas de Atuação *</Label>
                  <div className="grid grid-cols-2 gap-2 p-3 border rounded-sm max-h-48 overflow-y-auto">
                    {AREAS.map((area) => (
                      <div key={`edit-${area}`} className="flex items-center space-x-2">
                        <Checkbox
                          id={`edit-area-${area}`}
                          checked={editConsultant.areas_supported.includes(area)}
                          onCheckedChange={() => toggleEditArea(area)}
                        />
                        <Label htmlFor={`edit-area-${area}`} className="text-sm cursor-pointer">
                          {getAreaLabel(area)}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit-bio">Biografia</Label>
                  <Textarea
                    id="edit-bio"
                    value={editConsultant.bio}
                    onChange={(e) => setEditConsultant({ ...editConsultant, bio: e.target.value })}
                    placeholder="Breve descrição do consultor..."
                    className="h-20"
                    data-testid="edit-consultant-bio-input"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-navy hover:bg-navy/90"
                  disabled={savingEdit}
                  data-testid="edit-consultant-submit-btn"
                >
                  {savingEdit ? "Salvando..." : "Salvar Alterações"}
                </Button>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminConsultants;
